import type { Ref } from 'vue'
import { findActiveSeasonalEvent } from '~/composables/ui/useSeasonalTheme'
import { getSeasonalWelcomeModalCopy } from '~/utils/seasonalWelcome'
import { resolvePublicAppEnv } from '~/utils/appEnv'

export function getLocalDayKey(now = new Date()): string {
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function shouldShowNonProdEnvironmentNotice(appEnvRaw: unknown): boolean {
  const appEnv = resolvePublicAppEnv(appEnvRaw)
  return appEnv === 'dev' || appEnv === 'recette'
}

export function isLoggedInPrefsLoading(
  loggedIn: boolean,
  isUserPrefsLoading: boolean,
  isUserPrefsReady: boolean,
): boolean {
  return loggedIn && (isUserPrefsLoading || !isUserPrefsReady)
}

export function isNoticeReadyForOnboarding(
  loggedIn: boolean,
  me: { noticeUpToDate: boolean } | null,
): boolean {
  return !loggedIn || (me !== null && me.noticeUpToDate)
}

export function isAvatarModeReady(
  loggedIn: boolean,
  settingsReady: boolean,
  isConfigured: boolean,
): boolean {
  return !loggedIn || (settingsReady && isConfigured)
}

export function shouldOpenAvatarModeGate(
  loggedIn: boolean,
  isWelcomeResolved: boolean,
  isNoticeUpToDate: boolean,
  settingsReady: boolean,
  isConfigured: boolean,
): boolean {
  return loggedIn && isWelcomeResolved && isNoticeUpToDate && settingsReady && !isConfigured
}

export function shouldShowOnboardingRestart(
  loggedIn: boolean,
  hasReadCapability: boolean,
  isToolboxUiBlocked: boolean,
  isWelcomeResolved: boolean,
): boolean {
  return loggedIn && hasReadCapability && !isToolboxUiBlocked && isWelcomeResolved
}

export function areToolboxShortcutsEnabled(
  isToolboxUiBlocked: boolean,
  showSeasonalWelcome: boolean,
): boolean {
  return !isToolboxUiBlocked && !showSeasonalWelcome
}

export async function refreshAvatarSettingsIfLoggedIn(
  isLoggedIn: boolean,
  refreshMyAvatarSettings: () => Promise<void>,
): Promise<void> {
  if (!isLoggedIn) {
    return
  }
  await refreshMyAvatarSettings()
}

export async function confirmAvatarModeOnce(input: {
  isSavingAvatarMode: Ref<boolean>
  updateMyAvatarSettings: (payload: {
    avatarDisplayMode: 'teams' | 'avatar' | 'initials'
    avatarConfig?: Record<string, unknown> | null
  }) => Promise<void>
  payload: {
    avatarDisplayMode: 'teams' | 'avatar' | 'initials'
    avatarConfig: Record<string, unknown> | null
  }
}): Promise<void> {
  if (input.isSavingAvatarMode.value) {
    return
  }
  input.isSavingAvatarMode.value = true
  try {
    await input.updateMyAvatarSettings({
      avatarDisplayMode: input.payload.avatarDisplayMode,
      avatarConfig: input.payload.avatarConfig ?? undefined,
    })
  } finally {
    input.isSavingAvatarMode.value = false
  }
}

export async function startTrainingAndCloseCenter(input: {
  startTrainingById: (tourId: string, mode: 'do' | 'redo') => Promise<boolean>
  showTrainingCenter: Ref<boolean>
  payload: { tourId: string; mode: 'do' | 'redo' }
}): Promise<void> {
  const started = await input.startTrainingById(input.payload.tourId, input.payload.mode)
  if (!started) {
    return
  }
  input.showTrainingCenter.value = false
}

export function applyOverlayMountState(input: {
  shouldShowEnvironmentNotice: boolean
  betaNoticeAckDay: string | null
  deferSeasonalWelcomeAfterBeta: Ref<boolean>
  showBetaNotice: Ref<boolean>
  showSeasonalFooterLink: Ref<boolean>
  seasonalFooterShortLabel: Ref<string>
  now?: Date
}): void {
  const now = input.now ?? new Date()
  const betaNoticeMustShowFirst =
    input.shouldShowEnvironmentNotice && input.betaNoticeAckDay !== getLocalDayKey(now)
  input.deferSeasonalWelcomeAfterBeta.value = betaNoticeMustShowFirst
  input.showBetaNotice.value = betaNoticeMustShowFirst

  const seasonalEvent = findActiveSeasonalEvent(now)
  input.showSeasonalFooterLink.value = seasonalEvent !== null
  input.seasonalFooterShortLabel.value =
    seasonalEvent === null ? '' : getSeasonalWelcomeModalCopy(seasonalEvent.name).shortLabel
}
